import './DataGrid.css'
import { useCallback, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import type { DataGridState } from './createDataGridState'
import { cn } from '@/utilities/misc'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { Link } from 'react-router-dom'

export type DataGridProps<T> = {
  state: DataGridState<T>
  setState: GlobalStateSetter<DataGridState<T>> | Dispatch<SetStateAction<DataGridState<T>>>
  idColumn: keyof T
  columns: DataGridColumn<T>[]
}

export type DataGridRow<T> = {
  gridProps: DataGridProps<T>
  rowIndex: number
  row: T
}

export type DataGridColumn<T> = {
  id: T[keyof T]
  label?: ReactNode
  link?: (value: any, gridRow: DataGridRow<T>) => string | undefined
  align?: 'left' | 'center' | 'right'
  render?: (
    value: any,
    gridRow: DataGridRow<T>
  ) => ReactNode
  className?: (
    value: any,
    gridRow: DataGridRow<T>
  ) => string | undefined
  style?: (
    value: any,
    gridRow: DataGridRow<T>
  ) => React.CSSProperties | undefined
}

export function DataGrid<T>(gridProps: DataGridProps<T>) {

  const {
    state,
    setState,
    idColumn,
    columns,
  } = gridProps

  //--------------------------------------------------------------------------------
  function renderHeader(
    column: DataGridColumn<T>,
    columnIndex: number,
  ) {

    const className = cn(
      column.align && 'Align' + column.align
        .toString()
        .replace(/^[a-z]/, m => m.toUpperCase()),
    )

    return (
      <th
        key={String(columnIndex)}
        className={className}
      >
        {column.label}
      </th>
    )
  }

  //--------------------------------------------------------------------------------
  function renderCell(
    column: DataGridColumn<T>,
    columnIndex: number,
    rowIndex: number,
    row: T,
  ) {

    const dataRow: DataGridRow<T> = { gridProps, rowIndex, row }

    const cellClassName = column.className
      ? column.className(row[column.id as keyof T], dataRow)
      : undefined
    
    const cellStyle = column.style
      ? column.style(row[column.id as keyof T], dataRow)
      : undefined

    const className = cn(
      column.align && 'Align' + column.align
        .toString()
        .replace(/^[a-z]/, m => m.toUpperCase()),
      cellClassName,
    )

    const rawValue = row[column.id as keyof T]
    let cellValue = column.render
      ? column.render(rawValue, dataRow)
      : String(rawValue)
    
    if (column.link) {
      const linkTo = column.link(rawValue, dataRow)
      if (linkTo)
        cellValue = <Link to={linkTo}>{cellValue}</Link>
    }

    return (
      <td
        key={String(columnIndex)}
        className={className}
        style={cellStyle}
      >
        {cellValue}
      </td>
    )
  }

  //--------------------------------------------------------------------------------
  const defaultRenderRow = useCallback(({
    gridProps,
    rowIndex,
    row,
  }: DataGridRow<T>) => {
    const rowId = row[gridProps.idColumn]

    const className = cn(
      gridProps.state.selectedId === rowId && 'Selected',
    )

    return (
      <tr
        key={String(rowId)}
        className={className}
      >
        {gridProps.columns.map((column, columnIndex) =>
          renderCell(column, columnIndex, rowIndex, row)
        )}
      </tr>
    )
  }, [])

  //--------------------------------------------------------------------------------
  return (
    <table className='DataGrid'>
      <thead>
        <tr>
          {columns.map((column, columnIndex) => renderHeader(
            column,
            columnIndex,
          ))}
        </tr>
      </thead>
      <tbody>
        {state.rows.map((row, rowIndex) => defaultRenderRow({
          gridProps,
          rowIndex,
          row,
        }))}
      </tbody>
    </table>
  )
}
